import { useMemo, useState, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Clock,
  Calendar,
  Users,
  PieChart,
  Timer,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  ArrowUpDown
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import type { Task, GroupingState, SortingState, Priority } from '@/types';
import { getTimesheetData, getTeamMembers } from '@/utils/taskUtils';
import { formatDuration, getInitials, getAvatarColor } from '@/utils/formatters';
import { getTaskSortingOptions, createDefaultSortingState } from '@/utils/grouping';
import { ViewToolbar, ViewToggleGroup, NavButtons, StatCard, MultiGroupingSelector, MultiSortingSelector, MultiFilterSelector, ShowCompletedToggle } from '@/components/shared/view-toolbar';
import { getTaskGroupingOptions, createDefaultGroupingState } from '@/utils/grouping';
import { getAllFilterOptions } from '@/utils/filterOptions';
import { useProfilesContext } from '@/context/ProfilesContext';
import { Filter } from 'lucide-react';
import { CustomFieldsInline } from '@/components/fields/CustomFieldsInline';
import clsx from 'clsx';

import { useViewData } from '@/hooks/useViewData';
import { useWeeklyTimeEntries } from '@/hooks/useWeeklyTimeEntries';
import { useAppStore } from '@/store/appStore';
import { useTaskActions } from '@/hooks/useTaskActions';
import { normalizeStatus } from '@/constants/taskStatuses';

export function TimesheetView() {
  const {
  tasks,
  filters,
  grouping,
  sorting,
  onFilterChange,
  onGroupingChange,
  onSortingChange,
  onTaskClick,
  } = useViewData();
  const { handleUpdateTask: onUpdateTask } = useTaskActions();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [viewMode, setViewMode] = useState<'week' | 'team' | 'summary'>('week');
  const [localGrouping, setLocalGrouping] = useState<GroupingState>(() =>
  grouping || createDefaultGroupingState()
  );
  const [localSorting, setLocalSorting] = useState<SortingState>(() =>
  sorting || createDefaultSortingState()
  );

  const weekStart = useMemo(() => startOfWeek(currentWeek, { locale: it }), [currentWeek]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { locale: it }), [currentWeek]);
  const weekDays = useMemo(() => eachDayOfInterval({ start: weekStart, end: weekEnd }), [weekStart, weekEnd]);

  const { resolveDisplayName } = useProfilesContext();

  const timesheetData = useMemo(() => getTimesheetData(tasks), [tasks]);
  const teamMembers = useMemo(() => getTeamMembers(tasks, resolveDisplayName), [tasks, resolveDisplayName]);

  // Ore effettivamente registrate nella settimana (tabella time_entries)
  const weeklyTime = useWeeklyTimeEntries(weekStart, weekEnd);

  // Tasks for current week: quelli datati nella settimana più quelli su cui si
  // è registrato tempo, che altrimenti mancherebbero proprio nel timesheet
  const weekTasks = useMemo(() => {
  const weekStartTs = weekStart.getTime();
  const weekEndTs = weekEnd.getTime();
  return tasks.filter(task => {
      if (weeklyTime.byTask.has(task.id)) return true;
      const date = task.startDate || task.dueDate;
      if (!date) return false;
      return date >= weekStartTs && date <= weekEndTs;
  });
  }, [tasks, weekStart, weekEnd, weeklyTime.byTask]);

  // Time by day for current week
  const timeByDay = useMemo(() => {
  const dayTotals = new Map<string, number>();

  weekDays.forEach(day => {
      const key = format(day, 'yyyy-MM-dd');
      dayTotals.set(key, weeklyTime.byDay.get(key) || 0);
  });

  return dayTotals;
  }, [weeklyTime.byDay, weekDays]);

  // Stats
  const stats = useMemo(() => {
  const weekTimeSpent = weeklyTime.total;
  const weekTimeEstimated = weekTasks.reduce((sum, t) => sum + (t.timeEstimated || 0), 0);
  const efficiency = weekTimeEstimated > 0
      ? Math.round((weekTimeSpent / weekTimeEstimated) * 100)
      : 0;
  // Media sui soli giorni in cui si è lavorato: dividere sempre per sette
  // annacquerebbe la settimana di chi lavora tre giorni
  const daysWorked = Array.from(timeByDay.values()).filter(ms => ms > 0).length;
  const avgDailyTime = daysWorked > 0 ? Math.round(weekTimeSpent / daysWorked) : 0;

  return { weekTimeSpent, weekTimeEstimated, efficiency, avgDailyTime };
  }, [weekTasks, weeklyTime.total, timeByDay]);

  const goToPrevWeek = () => setCurrentWeek(prev => subWeeks(prev, 1));
  const goToNextWeek = () => setCurrentWeek(prev => addWeeks(prev, 1));
  const goToCurrentWeek = () => setCurrentWeek(new Date());


  // Grouping handlers
  const handleGroupingChange = useCallback((newGrouping: GroupingState) => {
  setLocalGrouping(newGrouping);
  onGroupingChange(newGrouping);
  }, [onGroupingChange]);

  // Sorting handlers
  const handleSortingChange = useCallback((newSorting: SortingState) => {
  setLocalSorting(newSorting);
  onSortingChange(newSorting);
  }, [onSortingChange]);

  // Unique values for filters
  const uniqueValues = useMemo(() => {
  return {
      statuses: Array.from(new Set(tasks.map(t => normalizeStatus(t.status)))).filter(Boolean),
      assignees: Array.from(new Set(tasks.flatMap(t => (t.assignees || []).filter(Boolean)))).filter((a): a is string => a !== null),
      tags: Array.from(new Set(tasks.flatMap(t => (t.tags || []).filter(Boolean)))).filter((t): t is string => t !== null),
      priorities: Array.from(new Set(tasks.map(t => t.priority).filter(Boolean))),
  };
  }, [tasks]);

  // Filter options with consistent ordering
  const filterOptions = useMemo(() => {
  return getAllFilterOptions(
      uniqueValues.statuses,
      uniqueValues.assignees,
      uniqueValues.tags,
      uniqueValues.priorities as Priority[],
      resolveDisplayName,
  );
  }, [uniqueValues, resolveDisplayName]);

  // Max time in a day for chart scaling
  const maxDayTime = useMemo(() => {
  return Math.max(...Array.from(timeByDay.values()), 1);
  }, [timeByDay]);

  return (
  <div className="flex-1 flex flex-col overflow-hidden animate-fade-in">
      {/* Header */}
      <ViewToolbar
    onOpenSidebar={() => useAppStore.getState().setMobileSidebarOpen(true)}
    icon={<Clock className="w-4 h-4 text-accent-emerald" />}
    title="Timesheet"
    subtitle="Monitoraggio tempo e attività"
    leftContent={
          <div className="flex items-center flex-wrap min-w-0 gap-3">
      {/* View Mode */}
      <ViewToggleGroup
              value={viewMode}
              onChange={(v) => setViewMode(v as 'week' | 'team' | 'summary')}
              options={[
        { value: 'week', icon: <Calendar className="w-3.5 h-3.5" />, label: 'Settimana' },
        { value: 'team', icon: <Users className="w-3.5 h-3.5" />, label: 'Team' },
        { value: 'summary', icon: <PieChart className="w-3.5 h-3.5" />, label: 'Riepilogo' },
              ]}
      />

      {/* Week Navigation */}
      <NavButtons
              onPrevious={goToPrevWeek}
              onNext={goToNextWeek}
              onToday={goToCurrentWeek}
              todayLabel="Oggi"
      />

      <span className="text-[12px] font-semibold text-text-primary capitalize">
              {format(weekStart, 'd MMM', { locale: it })} - {format(weekEnd, 'd MMM yyyy', { locale: it })}
      </span>
          </div>
    }
    rightContent={
          <div className="flex items-center flex-wrap justify-end gap-4">
      <div className="flex items-center gap-2">
              <Timer className="w-4 h-4 text-accent-emerald" />
              <span className="text-[11px] font-semibold text-text-primary tabular-nums">
        {formatDuration(stats.weekTimeSpent)}
              </span>
              <span className="text-[10px] text-text-quaternary">registrate</span>
      </div>
      {stats.efficiency > 0 && (
              <div className="flex items-center gap-1">
        {stats.efficiency > 100 ? (
                  <ArrowUpRight className="w-3.5 h-3.5 text-accent-rose" />
        ) : (
                  <ArrowDownRight className="w-3.5 h-3.5 text-accent-emerald" />
        )}
        <span className={clsx(
                  'text-[11px] font-semibold tabular-nums',
                  stats.efficiency > 100 ? 'text-accent-rose' : 'text-accent-emerald'
        )}>
                  {stats.efficiency}%
        </span>
        <span className="text-[10px] text-text-quaternary">efficienza</span>
              </div>
      )}

      {filters && onFilterChange && (
              <div className="flex items-center gap-2 gap-y-2 flex-wrap">
        <MultiFilterSelector
                  filters={{
          status: filters.status || [],
          assignees: filters.assignees || [],
          tags: filters.tags || [],
          priority: filters.priority || []
                  }}
                  onFilterChange={onFilterChange}
                  availableOptions={filterOptions}
                  icon={<Filter className="w-3.5 h-3.5 text-text-tertiary" />}
        />
        <MultiGroupingSelector
                  grouping={localGrouping}
                  onGroupingChange={handleGroupingChange}
                  availableOptions={getTaskGroupingOptions()}
                  icon={<Clock className="w-3.5 h-3.5 text-text-tertiary" />}
        />
        <MultiSortingSelector
                  sorting={localSorting}
                  onSortingChange={handleSortingChange}
                  availableOptions={getTaskSortingOptions()}
                  icon={<ArrowUpDown className="w-3.5 h-3.5 text-text-tertiary" />}
        />
        <ShowCompletedToggle
                  showCompleted={filters.showCompleted}
                  onToggle={(show) => onFilterChange({ showCompleted: show })}
        />
              </div>
      )}
          </div>
    }
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
    {viewMode === 'week' && (
          <WeekView
      weekDays={weekDays}
      timeByDay={timeByDay}
      timeByTask={weeklyTime.byTask}
      maxDayTime={maxDayTime}
      avgDailyTime={stats.avgDailyTime}
      weekTasks={weekTasks}
      onTaskClick={onTaskClick}
      onUpdateTask={onUpdateTask}
          />
    )}

    {viewMode === 'team' && (
          <TeamTimeView teamMembers={teamMembers} />
    )}

    {viewMode === 'summary' && (
          <SummaryView
      timesheetData={timesheetData}
      totalTasks={tasks.length}
      resolveDisplayName={resolveDisplayName}
          />
    )}
      </div>
  </div>
  );
}

interface WeekViewProps {
  weekDays: Date[];
  timeByDay: Map<string, number>;
  timeByTask: Map<string, number>;
  maxDayTime: number;
  avgDailyTime: number;
  weekTasks: Task[];
  onTaskClick?: (task: Task) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
}

function WeekView({ weekDays, timeByDay, timeByTask, maxDayTime, avgDailyTime, weekTasks, onTaskClick, onUpdateTask }: WeekViewProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const taskListRef = useRef<HTMLDivElement>(null);

  // Estimate height for task rows
  const TASK_ROW_ESTIMATE = 64;

  const rowVirtualizer = useVirtualizer({
  count: weekTasks.length,
  getScrollElement: () => taskListRef.current,
  estimateSize: () => TASK_ROW_ESTIMATE,
  overscan: 5,
  measureElement: (element) => element.getBoundingClientRect().height,
  });

  return (
  <div className="flex flex-col h-full gap-6">
      {/* Daily Chart */}
      <div className="p-4 rounded-xl bg-surface-secondary/50 border border-border-primary shrink-0">
    <div className="flex items-baseline justify-between gap-3 mb-4">
          <h3 className="text-[11px] uppercase tracking-wider text-text-quaternary font-semibold">
      Tempo per giorno
          </h3>
          {avgDailyTime > 0 && (
      <p className="text-[11px] text-text-tertiary tabular-nums">
              media <span className="font-semibold text-text-secondary">{formatDuration(avgDailyTime)}</span> nei giorni lavorati
      </p>
          )}
    </div>
    <div className="flex items-end gap-2 h-32">
          {weekDays.map(day => {
      const key = format(day, 'yyyy-MM-dd');
      const time = timeByDay.get(key) || 0;
      const height = maxDayTime > 0 ? (time / maxDayTime) * 100 : 0;
      const isToday = isSameDay(day, today);
      const isWeekend = day.getDay() === 0 || day.getDay() === 6;

      return (
              // h-full: il contenitore ha items-end, quindi senza questo la
              // colonna si stringe sull'altezza dell'etichetta e la barra,
              // che è alta in percentuale, resta a zero pixel
              <div key={key} className="flex-1 h-full flex flex-col items-center gap-2">
        <div className="flex-1 min-h-0 w-full flex items-end justify-center">
                  <div
          className={clsx(
                      'w-full max-w-[40px] rounded-t-lg transition-all',
                      isToday ? 'bg-accent-violet' : 'bg-accent-emerald/60',
                      time === 0 && 'bg-surface-tertiary/50'
          )}
          style={{ height: `${Math.max(height, 4)}%` }}
                  />
        </div>
        <div className="text-center">
                  <p className="text-[10px] text-text-quaternary uppercase">
          {format(day, 'EEE', { locale: it })}
                  </p>
                  <p className={clsx(
          'text-[11px] font-medium tabular-nums',
          isToday ? 'text-accent-violet' : isWeekend ? 'text-text-quaternary' : 'text-text-secondary'
                  )}>
          {format(day, 'd')}
                  </p>
                  {time > 0 && (
          <p className="text-[10px] text-text-tertiary tabular-nums">
                      {formatDuration(time)}
          </p>
                  )}
        </div>
              </div>
      );
          })}
    </div>
      </div>

      {/* Task List - Virtualized */}
      <div className="flex-1 flex flex-col min-h-0">
    <h3 className="text-[11px] uppercase tracking-wider text-text-quaternary font-semibold mb-3 shrink-0">
          Task della settimana ({weekTasks.length})
    </h3>
    {weekTasks.length === 0 ? (
          <p className="text-[12px] text-text-quaternary text-center py-8">
      Nessun task con tempo registrato questa settimana
          </p>
    ) : (
          <div
      ref={taskListRef}
      className="flex-1 overflow-y-auto"
      style={{ minHeight: '200px' }}
          >
      <div
              style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        width: '100%',
        position: 'relative',
              }}
      >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const task = weekTasks[virtualRow.index];
        return (
                  <div
          // Guard against occasional duplicate task IDs in the dataset
          key={`${task.id}-${virtualRow.index}`}
          ref={rowVirtualizer.measureElement}
          data-index={virtualRow.index}
          style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
          }}
                  >
          <TaskTimeRow
                      task={task}
                      timeSpent={timeByTask.get(task.id) || 0}
                      onClick={() => onTaskClick?.(task)}
                      onUpdate={onUpdateTask ? (updates) => onUpdateTask(task.id, updates) : undefined}
          />
                  </div>
        );
              })}
      </div>
          </div>
    )}
      </div>
  </div>
  );
}

interface TaskTimeRowProps {
  task: Task;
  /** Millisecondi registrati su questo task nella settimana mostrata. */
  timeSpent: number;
  onClick: () => void;
  onUpdate?: (updates: Partial<Task>) => void;
}

function TaskTimeRow({ task, timeSpent, onClick, onUpdate }: TaskTimeRowProps) {
  return (
  <div
      onClick={onClick}
      className="flex items-center gap-3 p-3 rounded-lg bg-surface-primary border border-border-primary hover:border-border-secondary cursor-pointer transition-all h-14 mb-2"
  >
      <div className="flex-1 min-w-0">
    <p className="text-[12px] font-medium text-text-primary truncate">{task.name}</p>
    <p className="text-[10px] text-text-quaternary truncate">{task.listName}</p>
      </div>

      <div className="flex items-center gap-4">
    {/* Custom Fields */}
    <div onClick={(e) => e.stopPropagation()}>
          <CustomFieldsInline
      task={task}
      onUpdate={onUpdate}
      maxDisplay={2}
      size="sm"
          />
    </div>

    {/* Registrate nella settimana */}
    {timeSpent > 0 && (
          <div className="text-right">
      <p className="text-[11px] text-accent-emerald tabular-nums">
              {formatDuration(timeSpent)}
      </p>
      <p className="text-[10px] text-text-quaternary">registrate</p>
          </div>
    )}

    {/* Estimated */}
    {task.timeEstimated && task.timeEstimated > 0 && (
          <div className="text-right">
      <p className="text-[11px] text-text-tertiary tabular-nums">
              {formatDuration(task.timeEstimated)}
      </p>
      <p className="text-[10px] text-text-quaternary">stimato</p>
          </div>
    )}

    {/* Assignees */}
    <div className="flex -space-x-1">
          {(task.assignees || []).slice(0, 2).map(a => (
      <div
              key={a}
              className={clsx(
        'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-surface-primary',
        getAvatarColor(a)
              )}
      >
              {getInitials(a)}
      </div>
          ))}
    </div>
      </div>
  </div>
  );
}

interface TeamTimeViewProps {
  teamMembers: ReturnType<typeof getTeamMembers>;
}

function TeamTimeView({ teamMembers }: TeamTimeViewProps) {
  const maxTime = Math.max(...teamMembers.map(m => m.totalTimeEstimated), 1);
  const parentRef = useRef<HTMLDivElement>(null);

  // Estimate height for member rows
  const MEMBER_ROW_ESTIMATE = 100;

  const rowVirtualizer = useVirtualizer({
  count: teamMembers.length,
  getScrollElement: () => parentRef.current,
  estimateSize: () => MEMBER_ROW_ESTIMATE,
  overscan: 5,
  measureElement: (element) => element.getBoundingClientRect().height,
  });

  return (
  <div className="flex flex-col h-full">
      <h3 className="text-[11px] uppercase tracking-wider text-text-quaternary font-semibold mb-4 shrink-0">
    Tempo per membro del team ({teamMembers.length})
      </h3>

      <div ref={parentRef} className="flex-1 overflow-y-auto" style={{ minHeight: '300px' }}>
    <div
          style={{
      height: `${rowVirtualizer.getTotalSize()}px`,
      width: '100%',
      position: 'relative',
          }}
    >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
      const member = teamMembers[virtualRow.index];
      const percentage = (member.totalTimeEstimated / maxTime) * 100;

      return (
              <div
        key={member.name}
        ref={rowVirtualizer.measureElement}
        data-index={virtualRow.index}
        style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
        }}
              >
        <div className="p-4 rounded-xl bg-surface-primary border border-border-primary mb-3">
                  <div className="flex items-center gap-3 mb-3">
          <div className={clsx(
                      'w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white',
                      getAvatarColor(member.name)
          )}>
                      {getInitials(member.name)}
          </div>
          <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-text-primary truncate">{member.name}</p>
                      <p className="text-[10px] text-text-quaternary">{member.totalTasks} task</p>
          </div>
          <div className="text-right">
                      <p className="text-[14px] font-bold text-text-primary tabular-nums">
            {formatDuration(member.totalTimeEstimated)}
                      </p>
          </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="h-2 bg-surface-tertiary rounded-full overflow-hidden">
          <div
                      className="h-full bg-accent-emerald rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
          />
                  </div>
        </div>
              </div>
      );
          })}
    </div>
      </div>
  </div>
  );
}

interface SummaryViewProps {
  timesheetData: ReturnType<typeof getTimesheetData>;
  totalTasks: number;
  resolveDisplayName: (id: string) => string;
}

function SummaryView({ timesheetData, totalTasks, resolveDisplayName }: SummaryViewProps) {
  const tasksWithTime = Array.from(timesheetData.tasksByMember.entries())
  .reduce((sum, [, data]) => sum + data.tasks, 0);

  const sortedContributors = useMemo(() =>
  Array.from(timesheetData.tasksByMember.entries())
      .sort((a, b) => b[1].tasks - a[1].tasks),
  [timesheetData.tasksByMember]
  );

  const parentRef = useRef<HTMLDivElement>(null);

  // Estimate height for contributor rows
  const CONTRIBUTOR_ROW_ESTIMATE = 68;

  const rowVirtualizer = useVirtualizer({
  count: sortedContributors.length,
  getScrollElement: () => parentRef.current,
  estimateSize: () => CONTRIBUTOR_ROW_ESTIMATE,
  overscan: 5,
  measureElement: (element) => element.getBoundingClientRect().height,
  });

  return (
  <div className="flex flex-col h-full gap-6">
      {/* Overall Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 shrink-0">
    <StatCard
          icon={<Clock className="w-5 h-5" />}
          label="Tempo totale stimato"
          value={formatDuration(timesheetData.totalTimeEstimated)}
          color="blue"
    />
    <StatCard
          icon={<Sparkles className="w-5 h-5" />}
          label="Task con tempo"
          value={tasksWithTime.toString()}
          subValue={`su ${totalTasks} totali`}
          color="violet"
    />
      </div>

      {/* Top Contributors - Virtualized */}
      <div className="flex-1 flex flex-col min-h-0">
    <h3 className="text-[11px] uppercase tracking-wider text-text-quaternary font-semibold mb-3 shrink-0">
          Contributo per membro ({sortedContributors.length})
    </h3>
    <div ref={parentRef} className="flex-1 overflow-y-auto" style={{ minHeight: '200px' }}>
          <div
      style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
      }}
          >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const [userId, data] = sortedContributors[virtualRow.index];
              const name = resolveDisplayName(userId);
              const totalTasks = Array.from(timesheetData.tasksByMember.values()).reduce((sum, d) => sum + d.tasks, 0);
              const percentage = totalTasks > 0
        ? (data.tasks / totalTasks) * 100
        : 0;

              return (
        <div
                  key={userId}
                  ref={rowVirtualizer.measureElement}
                  data-index={virtualRow.index}
                  style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          transform: `translateY(${virtualRow.start}px)`,
                  }}
        >
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-primary border border-border-primary h-[60px] mb-2">
          <div className={clsx(
                      'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0',
                      getAvatarColor(name)
          )}>
                      {getInitials(name)}
          </div>
          <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-medium text-text-primary truncate">{name}</p>
                      <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                          <div
              className="h-full bg-accent-violet rounded-full"
              style={{ width: `${percentage}%` }}
                          />
            </div>
            <span className="text-[10px] text-text-quaternary tabular-nums w-10 text-right shrink-0">
                          {Math.round(percentage)}%
            </span>
                      </div>
          </div>
          <div className="text-right shrink-0">
                      <p className="text-[12px] font-semibold text-text-primary tabular-nums">
            {data.tasks} task
                      </p>
                      <p className="text-[10px] text-text-quaternary">{data.tasks} task</p>
          </div>
                  </div>
        </div>
              );
      })}
          </div>
    </div>
      </div>
  </div>
  );
}


